import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db, useFirestore } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import type { SlackWorkspaceConnection } from '../types/models';

/**
 * Reactive list of connected Slack workspaces, backed by
 * `slackConnections/{uid}/workspaces` (written by the
 * `connectSlackWorkspace` / `disconnectSlackWorkspace` /
 * `setSlackWorkspaceClient` callables). Unlike Gmail (a single doc), this is
 * a subcollection since several workspaces can be connected at once.
 */
export function useSlackConnections(): SlackWorkspaceConnection[] {
  const { user } = useAuth();
  const [connections, setConnections] = useState<SlackWorkspaceConnection[]>([]);

  useEffect(() => {
    if (!useFirestore || !db || !user) return;
    return onSnapshot(collection(db, 'slackConnections', user.uid, 'workspaces'), (snap) => {
      setConnections(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            teamId: d.id,
            teamName: String(data.teamName ?? d.id),
            clientId: (data.clientId as string | null | undefined) ?? null,
            needsReauth: Boolean(data.needsReauth),
          };
        }),
      );
    });
  }, [user]);

  // Reset immediately (not from inside the effect) when there's no signed-in
  // user to subscribe for, e.g. after sign-out.
  if (!user && connections.length > 0) setConnections([]);

  return connections;
}
